/**
 * ChatPage Component
 * Main page for chats with sidebar, routing, and member management
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Drawer, Empty } from 'antd';
import { PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { useLayoutConfig } from '@/components/common/ProtectedLayout';
import { ChatListSidebar } from '@/components/features/chat/ChatListSidebar';
import { GroupCreationModal } from '@/components/features/chat/GroupCreationModal';
import { ChatMembersPanel } from '@/components/features/chat/ChatMembersPanel';
import { LightChatContainer } from '@/components/features/chat/LightChatContainer';
import { useChat } from '@/hooks/useChat';
import { ChatType } from '@/api/types/chat.types';
import type { Chat } from '@/api/types/chat.types';
import './ChatPage.css';

export const ChatPage: React.FC = () => {
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const { setNoPadding } = useLayoutConfig();

  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showMembersDrawer, setShowMembersDrawer] = useState(false);

  // Fetch selected chat details
  const { data: selectedChat, isLoading: loadingChat } = useChat(
    chatId || null
  );

  // Set noPadding mode for full-height chat
  useEffect(() => {
    setNoPadding(true);
    return () => setNoPadding(false);
  }, [setNoPadding]);

  // If no chat is selected, redirect to general chat (or show empty state)
  useEffect(() => {
    if (!chatId) {
      // Redirect to general chat by default
      // In Phase 9 we'll implement auto-join general chat on mount
      navigate('/apps/chat/general', { replace: true });
    }
  }, [chatId, navigate]);

  const handleChatSelect = (chat: Chat) => {
    navigate(`/apps/chat/${chat.id}`);
  };

  const handleGroupCreated = (chat: Chat) => {
    setShowGroupModal(false);
    navigate(`/apps/chat/${chat.id}`);
  };

  return (
    <div className="chat-page-container">
      {/* Left Sidebar: Chat List */}
      <div className="chat-page-sidebar">
        <div className="chat-page-sidebar-header">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowGroupModal(true)}
            block
          >
            Create Group
          </Button>
        </div>
        <ChatListSidebar
          selectedChatId={chatId}
          onChatSelect={handleChatSelect}
        />
      </div>

      {/* Main Content: Chat Messages */}
      <div className="chat-page-content">
        {chatId === 'general' ? (
          // General chat (legacy instance-based)
          <LightChatContainer
            instanceType="general"
            instanceId="00000000-0000-0000-0000-000000000000"
            title="General Chat"
            hideHeader={false}
            autoFocus={true}
          />
        ) : chatId && selectedChat ? (
          // Specific chat (new chat types system)
          <>
            <div className="chat-page-header">
              <div className="chat-page-title">
                <h2>{selectedChat.title}</h2>
                {selectedChat.description && (
                  <p className="chat-description">{selectedChat.description}</p>
                )}
              </div>
              {selectedChat.chat_type === ChatType.GROUP && (
                <Button
                  icon={<TeamOutlined />}
                  onClick={() => setShowMembersDrawer(true)}
                >
                  Members ({selectedChat.participant_count || 0})
                </Button>
              )}
            </div>
            <LightChatContainer
              instanceType={selectedChat.instance_type || 'chat'}
              instanceId={selectedChat.instance_id || chatId}
              title={selectedChat.title}
              hideHeader={true}
              autoFocus={true}
            />
          </>
        ) : loadingChat ? (
          <div className="chat-page-loading">Loading chat...</div>
        ) : (
          <Empty
            description="Select a chat to start messaging"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </div>

      {/* Group Creation Modal */}
      <GroupCreationModal
        open={showGroupModal}
        onCancel={() => setShowGroupModal(false)}
        onSuccess={handleGroupCreated}
      />

      {/* Members Drawer */}
      {selectedChat && (
        <Drawer
          title="Chat Members"
          placement="right"
          open={showMembersDrawer}
          onClose={() => setShowMembersDrawer(false)}
          width={400}
        >
          <ChatMembersPanel
            chatId={selectedChat.id}
            myRole={selectedChat.my_role || null}
          />
        </Drawer>
      )}
    </div>
  );
};
